import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, ShieldAlert, ShieldX } from "lucide-react";

import { fetchRelatedChecks } from "../lib/api";

const VERDICT_STYLE = {
  AVOID: { label: "AVOID", color: "#b91c1c", bg: "#fdeeea", Icon: ShieldX },
  USE_WITH_CAUTION: { label: "USE WITH CAUTION", color: "#b45309", bg: "#fdf3df", Icon: ShieldAlert },
  SAFE_TOGETHER: { label: "No label conflict found", color: "#0e6b34", bg: "#eefbf2", Icon: ShieldCheck },
  INSUFFICIENT_DATA: { label: "NOT ENOUGH VERIFIED DATA", color: "#7a7468", bg: "#f5ecde", Icon: ShieldAlert },
};

/**
 * RelatedChecksSection — three popular "Also check:" rows surfaced after a
 * result. Same medication, different product, with the cached verdict so
 * the user has a hint before they tap. Each row deep-links to /check with
 * pre-filled params so the check runs immediately.
 *
 * Hidden when the medication has no other checked products in history.
 */
export default function RelatedChecksSection({ result }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  const medId = result?.medication?.id
    || (result?.medications && result.medications[0]?.id)
    || "";
  const excludeProduct = result?.product_name || "";

  useEffect(() => {
    if (!medId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchRelatedChecks({
          medicationId: medId,
          excludeProduct,
          limit: 3,
        });
        if (cancelled) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [medId, excludeProduct]);

  if (!medId || !items.length) return null;

  const medName = result?.medication?.name
    || result?.medication?.generic_name
    || (result?.medications && result.medications[0]?.name)
    || medId;

  const handleClick = (it) => {
    const params = new URLSearchParams();
    params.set("med", medId);
    const productSlug = it.product_id || it.product_name;
    if (productSlug) params.set("product", productSlug);
    navigate(`/check?${params.toString()}`);
  };

  return (
    <section
      data-testid="related-checks-section"
      className="mt-6 rounded-3xl border bg-white p-5 sm:p-6 animate-fade-up"
      style={{
        borderColor: "rgba(31,61,46,0.10)",
        boxShadow: "0 1px 2px rgba(0,0,0,0.03), 0 8px 24px -16px rgba(0,0,0,0.10)",
      }}
    >
      <header className="mb-4">
        <p className="text-[10.5px] uppercase tracking-[0.24em] font-bold text-ink-mute">
          Also worth checking
        </p>
        <h3 className="mt-1 text-[16px] sm:text-[17px] font-extrabold text-ink leading-snug">
          What others checked with {medName}
        </h3>
      </header>

      <ul className="space-y-2.5">
        {items.map((it, idx) => {
          const style = VERDICT_STYLE[it.verdict] || VERDICT_STYLE.INSUFFICIENT_DATA;
          const Icon = style.Icon;
          return (
            <li key={`${it.product_name}-${idx}`}>
              <button
                type="button"
                data-testid={`related-check-${idx}`}
                onClick={() => handleClick(it)}
                className="w-full flex items-center gap-3 rounded-2xl bg-cream-50 border border-surface-line px-3.5 py-3 text-left transition-all hover:border-ink/20 hover:bg-cream-100 hover:shadow-subtle"
              >
                <span
                  className="inline-flex items-center justify-center w-9 h-9 rounded-xl shrink-0"
                  style={{ background: style.bg, color: style.color }}
                >
                  <Icon className="w-4 h-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-semibold text-ink truncate">
                    {medName} × {it.product_name}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-[11px]">
                    <span
                      className="font-bold uppercase tracking-[0.18em]"
                      style={{ color: style.color }}
                    >
                      {style.label}
                    </span>
                    <span className="text-ink-mute tabular-nums">
                      · {it.count} checks
                    </span>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-ink-light shrink-0" />
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
